import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { IonHeader, IonIcon, IonSpinner } from "@ionic/react";
import Page from "../components/Page";
import { chevronBackOutline, chevronDownOutline } from "ionicons/icons";
import { bindActionCreators } from "@reduxjs/toolkit";
import withNav from "../utils/hoc/withNav";
import ToastService from "../services/ToastService";
import * as CartActions from "../actions/CartActions";
import { storeXookData } from "../actions/XookActions";
import { connect } from "react-redux";
import ErrorComponent from "../components/Error";
import UserService from "../services/UserService";
import { Modal, ModalOverlay, ModalButtonGroup, ModalHeader, ModalPrimaryButton, ModalSubtitle } from "../components/Modal";
import { PrimaryText } from "../components/Texts";
import ExpandableContent from "../components/ExpandableContent";


const TERMS_CONTENT_TYPE = {
	PARAGRAPH: "paragraph",
	LIST: "list",
};

const Center = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
`;

const Error = styled.div`
	font-weight: 600;
	font-size: 20px;
	color: #fff;
	width: 100%;
`;

const PageHeader = styled(IonHeader)`
	position: fixed;
	margin: 0 0 0 0;
	background: ${({ theme }) => theme.colors.background};
	color: #f2f2f2;
	font-style: normal;
	font-size: 20px;
	display: flex;
	align-items: center;
	padding: 1.5rem 1rem;
	border: none;
	box-shadow: none;
	&:after {
		background-image: none;
	}
`;

const BackButton = styled.div`
	margin: 2px 0.5rem 0 -5px;
`;

const BackButtonImg = styled(IonIcon)`
	width: 24px;
	height: 24px;
	color: #fff;
`;

const PageTitle = styled.div`
	font-style: normal;
	font-weight: 900;
	font-size: 20px;
	letter-spacing: 0px;
	display: flex;
	justify-content: center;
`;

const HelpButton = styled(IonIcon)`
	width: 20px;
	height: 20px;
	margin-left: auto;
`;

const PaddedElement = styled.div`
	margin: 4rem 0 0 0;
`;

const CouponsCard = styled.div<any>`
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	border-radius: 6px;
	margin: 1rem 0 0 0;
	padding: 0 1rem;
	background: #4b4b4b;
`;

const CouponContainer = styled.div<any>`
	position: relative;
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.5rem;
	overflow: hidden;
	padding: 1rem 0 1rem 0; //Adjust the bottom gap.
	&:not(:first-child) {
		border-top: 1px dotted #f2f2f2;
	}
	transition: max-height 0.3s ease-out;
`;

const CouponTitle = styled.div`
	color: #e8e8e8;
	font-size: 18px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
`;

const CouponSubTitle = styled.div`
	color: #fc0;
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
`;

const CouponDescription = styled.div`
	text-align: left;
	color: #d6d6d6;
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
	width: 100%;
	transition: inherit;
`;

const CouponAction = styled.div<any>`
	text-align: right;
	font-size: 12px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
	${(props) =>
		props.allow &&
		css`
			color: #ffcc00;
		`}
	${(props) =>
		props.applied &&
		css`
			color: #5fff5b;
		`}
    ${(props) =>
		props.notApplicable &&
		css`
			color: #fc6b6b;
		`}
`;

const CouponExpander = styled(IonIcon)`
	font-size: 14px;
	color: #f2f2f2;
	transition: inherit;
`;

const Row = styled.div<any>`
	display: flex;
	align-items: ${(props) => (props.center ? "center" : "")};
	justify-content: space-between;
	width: 100%;
	gap: 1rem;
	margin: ${(props) => props.margin};
	min-height: ${(props) => props.minHeight};
`;

const TermsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const Coupons = (props: any) => {
	const { kioskId } = props.match.params;

	const { setCart, coupons, navigate, dataLoaded, storeXookData, menuData, currency } = props;

	const [componentsLoading, setComponentsLoading] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [couponError, setCouponError] = useState("");

	async function getCoupons() {
		setLoading(true);
		const response = await UserService.getCoupons(kioskId, { menuId: menuData?.id });
		const { data, error } = response;
		if (data) {
			storeXookData({ coupons: data, couponsLoaded: true });
		}
		if (error) {
			setError(true);
		}
		setLoading(false);
	}

	useEffect(() => {
		if (!dataLoaded) goBack();
		getCoupons();
	}, []);

	function goBack() {
		navigate.push(`/kiosk/${kioskId}/cart`);
	}

	function handleComponentLoading(componentId: any, isLoading: boolean) {
		const alreadyAdded = componentsLoading.includes(componentId);
		if (isLoading && !alreadyAdded) {
			setComponentsLoading((prev: any) => [...prev, componentId]);
		}
		if (!isLoading) {
			setComponentsLoading((prev: any) => prev.filter((component: any) => component !== componentId));
		}
	}

	function renderCoupons() {
		if (coupons.length) {
			let renderData = [];
			for (const coupon of coupons) {
				const { title, offerCode, description, id, saving, termsCondition } = coupon;
				const isLoading = componentsLoading.includes(id);
				let couponActionElement;
				const termsAndConditions =  termsCondition?.length ? termsCondition : ""; //termsCondition is a html string.

				couponActionElement = (
					<CouponAction allow={true} onClick={() => addCoupon(coupon)}>
						Apply
					</CouponAction>
				);

				const couponCard = (
					<CouponContainer disabled={isLoading} id={`${id}-container`}>
						<Row center>
							<CouponTitle>{title || offerCode}</CouponTitle>
							{isLoading ? <IonSpinner name="dots"></IonSpinner> : couponActionElement}
						</Row>

						<CouponSubTitle>{saving ? `Save ${currency}${saving} using this coupon ` : ""}</CouponSubTitle>

						<ExpandableContent maxHeightConstraint="15" >
							<Row>
								<CouponDescription>
									{description}
								</CouponDescription>
							</Row>
							{termsAndConditions?.length ? (
								<TermsContainer dangerouslySetInnerHTML={{ __html: termsAndConditions }} />
							) : (
								""
							)}
						</ExpandableContent>
					</CouponContainer>
				);

				renderData.push(couponCard);
			}

			if (renderData.length) {
				return <CouponsCard>{renderData}</CouponsCard>
			}
		} 

		return (
			<Center>
				<Error>No Coupons Available</Error>
			</Center>
		);

	}

	async function addCoupon(couponData: any) {
		const { id: couponId, offerCode } = couponData;

		handleComponentLoading(couponId, true);
		const payload = { couponId, menuId: menuData?.id };
		const { data, error } = await UserService.addCoupon(kioskId, payload);
		handleComponentLoading(couponId, false);
		if (data) {
			const { cart, isValid } = data;
			if (!isValid) {
				setCouponError(`Could not add coupon ${offerCode} as it is no longer valid.`);
				return;
			}
			setCart(cart);
			goBack();
			storeXookData({ coupons: [], couponsLoaded: false });
		}
		if (error) {
			ToastService.showToast(error);
		}
	}

	return (
		<Page>
			<PageHeader>
				<BackButton onClick={goBack}>
					<BackButtonImg src={chevronBackOutline} />
				</BackButton>
				<PageTitle>Coupons</PageTitle>
				<HelpButton />
			</PageHeader>
			<PaddedElement />
			{!loading && !error ? renderCoupons() : ""}
			{loading && (
				<Center>
					<IonSpinner name="dots" color={"#F2F2F2"}></IonSpinner>
				</Center>
			)}
			{couponError ? (
				<ModalOverlay>
					<Modal>
						<ModalHeader>Oops!</ModalHeader>
						<ModalSubtitle>{couponError}</ModalSubtitle>
						<ModalButtonGroup>
							<ModalPrimaryButton
								onClick={() => {
									setCouponError("");
								}}>
								Okay
							</ModalPrimaryButton>
						</ModalButtonGroup>
					</Modal>
				</ModalOverlay>
			) : (
				""
			)}
			{error && !loading && <ErrorComponent code={""} />}
		</Page>
	);
};

const mapStateToProps = (state: any) => {
	const {
		Cart,
		Xook,
		Food: { menuData },
	} = state;
	return { ...Cart, ...Xook, menuData };
};

const mapDispatchToProps = (dispatch: any) => ({
	...bindActionCreators(
		{
			...CartActions,
			storeXookData,
		},
		dispatch
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(withNav(Coupons));
